'use client'

import {
  Enum_Exhibition_Type,
  ExhibitionArtistItemFragment,
  GetSimilarLiveExhibitionsQuery,
  SimilarExhibitionItemFragment
} from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { PaginationButton, PaginationDirection } from '@components/pagination-button'
import { safeModulo } from '@utils/math'
import { allExhibitionsRoute, exhibitionShowRoute } from '@utils/routes'
import { BodyText, BrandText, HeadlineText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { AnimatePresence, motion } from 'motion/react'

import React from 'react'
import { buttonVariants } from '@components/button'

const MoreExhibitionsItem = ({ exhibition }: { exhibition?: SimilarExhibitionItemFragment | null }) => {
  const artists: { artist?: ExhibitionArtistItemFragment | null }[] = []
  exhibition?.Collections.forEach((collection) =>
    collection?.Collection?.Project?.artistPage?.data.forEach((artist) => artists.push({ artist: artist.attributes }))
  )

  // sort artists by artist name
  artists.sort((a, b) => {
    if (!a?.artist || !b?.artist) {
      return 0
    }
    return a.artist.artistName > b.artist.artistName ? 1 : -1
  })

  const slicedArtists = artists.slice(0, exhibition?.Type === Enum_Exhibition_Type.Solo ? 1 : artists.length)
  return (
    <AllPurposeLink
      href={exhibitionShowRoute(exhibition?.slug ?? '')}
      className="gap-y-sm relative flex h-full w-full flex-col"
    >
      <div className="bg-surface-low-inverted p-md relative aspect-square w-full overflow-hidden">
        <div className="relative aspect-square w-full overflow-hidden">
          <ImageWithCustomLoader
            src={exhibition?.HeaderImage?.data?.attributes?.url ?? ''}
            style={{
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            fill
            alt={`Thumbnail for exhibition: ${exhibition?.Title}`}
          />
        </div>
      </div>
      <div className="gap-y-2xs flex flex-col">
        <BodyText className="text-on-surface-inverted text-center font-medium">{exhibition?.Title}</BodyText>
        <BodyText className="text-on-surface-inverted text-center">
          {slicedArtists.map((a, index) => `${a.artist?.artistName}${index + 1 !== slicedArtists.length ? ', ' : ''}`)}
        </BodyText>
      </div>
    </AllPurposeLink>
  )
}
export const MoreExhibitions = ({
  exhibitions
}: {
  exhibitions: GetSimilarLiveExhibitionsQuery | undefined
}): React.ReactElement => {
  const [currIndex, setCurrIndex] = React.useState<number>(0)

  const exhs = exhibitions?.exhibitions?.data

  const changeItem = (direction: PaginationDirection) => {
    // This is a hack to prevent the index from going out of bounds if the user goes
    // right then index should increase until it gets to the end and then it should be reset
    // to the beginning, if left then it should decrease until it gets to 0 then reset
    // to the end
    setCurrIndex(safeModulo(currIndex + (direction === PaginationDirection.Right ? 1 : -1), exhs?.length ?? 0))
  }
  return (
    <div className="px-md bg-surface-inverted py-xl gap-y-xl flex w-full flex-col items-center justify-center">
      <HeadlineText className="text-on-surface-inverted font-light">Discover more exhibitions</HeadlineText>
      <div className="hidden w-full flex-1 items-center justify-center gap-[80px] md:flex">
        {exhibitions?.exhibitions?.data?.map((e) => {
          const exh = e.attributes
          if (!exh) return null
          return (
            <div key={exh.slug} className="relative h-full w-full max-w-[500px]">
              <MoreExhibitionsItem exhibition={exh} />
            </div>
          )
        })}
      </div>
      <div className="flex w-full items-center justify-between md:hidden">
        {exhs?.length && exhs?.length > 1 ? (
          <PaginationButton
            direction={PaginationDirection.Left}
            onClick={() => changeItem(PaginationDirection.Left)}
            className="bg-primary-inverted-10 rounded-full"
          />
        ) : null}
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3
            }}
            key={exhs?.[currIndex]?.attributes?.slug}
            className="px-sm w-full"
          >
            {exhs?.[currIndex]?.attributes ? <MoreExhibitionsItem exhibition={exhs[currIndex].attributes} /> : null}
          </motion.div>
        </AnimatePresence>
        {exhs?.length && exhs?.length > 1 ? (
          <PaginationButton
            direction={PaginationDirection.Right}
            onClick={() => changeItem(PaginationDirection.Right)}
            className="bg-primary-inverted-10 rounded-full"
          />
        ) : null}
      </div>
      <AllPurposeLink
        href={allExhibitionsRoute()}
        className={buttonVariants({ colorTheme: 'inverted-secondary', branded: true })}
      >
        <BrandText>BROWSE ALL</BrandText>
      </AllPurposeLink>
    </div>
  )
}
